import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  ClickAwayListener,
  Modal,
  Snackbar,
  styled,
  TextField,
  useTheme,
} from "@mui/material";
import { ConnectionType } from "protico-sdk";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { ensContext } from "../../contexts/ENSContext";
import useAlert from "../../hooks/useAlert";
import { useEventWebsocket } from "../../hooks/useEventWebsocket";
import useSocialConnection from "../../hooks/useSocialConnection";
import { TaskStateType } from "../../types/types";
import { getProticoClient } from "../../utils/api";
import { checkAddress, checkENS } from "../../utils/format.utils";
import { ConfirmButton, ModalContainer } from "./AddFriendModal.styles";
import { useWeb3Auth } from "../../hooks/useWeb3Auth";

const BlackBackdrop = styled(Backdrop, {
  name: "MuiModal",
  slot: "Backdrop",
  overridesResolver: (props, styles) => {
    return styles.backdrop;
  },
})({
  backgroundColor: "rgba(8, 8, 8, 0.9)",
  zIndex: -1,
});

const AddFriendModal = ({
  open,
  onClose,
  address,
}: {
  open: any;
  onClose: any;
  address: string;
}) => {
  const { t } = useTranslation();
  const [addressValue, setAddressValue] = useState(address || "");
  const [nicknameValue, setNicknameValue] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const { refetch: renewUserSocialConnection, contacts } =
    useSocialConnection();
  const theme = useTheme();
  const [isOpen, alertContent, isError, setOptionalAlertContent] = useAlert();
  const { ens } = useContext(ensContext);
  const { sendEvent } = useEventWebsocket();
  const { address: userAddress } = useWeb3Auth();
  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let requestValue = addressValue;
    try {
      if (checkENS(addressValue)) {
        requestValue = await ens.getOwner(addressValue);
        if (requestValue === "0x0000000000000000000000000000000000000000") {
          setOptionalAlertContent(
            t("Alert_Content_ENS_No_Owner"),
            TaskStateType.FAIL
          );
          return;
        }
      }
      checkAddress(requestValue);
    } catch (_) {
      setOptionalAlertContent(
        t("Alert_Content_Incorrect_Address"),
        TaskStateType.FAIL
      );
      return;
    }
    // TODO filter nicknameValue over 30 letters
    setIsFetching(true);
    try {
      const ensName = await ens.getName(requestValue);
      const addedContact = await getProticoClient().contact.addOne(
        userAddress,
        {
          connectionType: ConnectionType.CONNECT,
          target: requestValue,
          alias: nicknameValue ? nicknameValue : ensName?.name ?? "",
        }
      );
      // addOne upserts the contact, so we need to filter out the old one
      const newConnections = contacts.filter(
        (c) => c.target !== addedContact.target
      );
      renewUserSocialConnection([...newConnections, addedContact]);
    } catch (e) {
      setOptionalAlertContent(
        t("Alert_Content_Profile_Update_Fail"),
        TaskStateType.FAIL
      );
      setIsFetching(false);
      return;
    }
    // Update Connections successfully
    sendEvent("connection", "requested", requestValue);
    setIsFetching(false);
    setOptionalAlertContent(t("Alert_Content_Success"), TaskStateType.SUCCESS);
    setTimeout(() => {
      onClose();
      setAddressValue("");
      setNicknameValue("");
    }, 1300);
  };

  return (
    <React.Fragment>
      <Modal
        hideBackdrop={true}
        open={open}
        onClose={onClose}
        aria-labelledby="child-modal-title"
      >
        <BlackBackdrop open={true}>
          <ModalContainer>
            <Snackbar
              open={isOpen}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert
                variant="filled"
                severity={isError ? "error" : "success"}
                color={isError ? "error" : "info"}
              >
                <strong>{alertContent}</strong>
              </Alert>
            </Snackbar>{" "}
            <ClickAwayListener
              onClickAway={() => {
                onClose();
              }}
            >
              <Card
                style={{
                  display: "flex",
                  background: "#111",
                  border: "1px solid #333",
                  width: "70%",
                  maxWidth: "475px",
                  marginTop: "10px",
                  padding: "20px",
                  flexGrow: 1,
                  flexDirection: "column",
                  position: "absolute" as "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  // max-height: 85vh;
                  alignItems: "center",
                }}
              >
                <h3
                  style={{
                    textAlign: "center",
                    color: "#ddd",
                    marginBottom: "25px",
                  }}
                >
                  {t("FlashChat_Profile_Add_Friend")}
                </h3>
                <form
                  onSubmit={onSubmit}
                  style={{
                    display: "flex",
                    flexGrow: 1,
                    flexDirection: "column",
                    width: "80%",
                    alignItems: "center",
                  }}
                >
                  <TextField
                    required
                    fullWidth
                    disabled={address !== ""}
                    id="outlined-required"
                    label={t("FlashChat_Profile_Add_Friend_Address")}
                    value={addressValue}
                    onChange={(e) => {
                      setAddressValue(e.target.value);
                    }}
                    style={{
                      marginBottom: "5px",
                    }}
                    margin="dense"
                    variant="standard"
                  />
                  <TextField
                    fullWidth
                    id="outlined-required"
                    label={t("FlashChat_Profile_Add_Friend_Nickname")}
                    value={nicknameValue}
                    onChange={(e) => {
                      setNicknameValue(e.target.value);
                    }}
                    helperText={t(
                      "FlashChat_Profile_Add_Friend_Nickname_HelperText"
                    )}
                    style={{
                      marginBottom: "25px",
                    }}
                    variant="standard"
                  />
                  {isFetching ? (
                    <CircularProgress
                      style={{
                        color: theme.brandColor,
                        marginTop: "5px",
                        padding: "8px",
                        width: "35px",
                        height: "35px",
                      }}
                    />
                  ) : (
                    <ConfirmButton
                      type="submit"
                      ifEmptyAddress={addressValue === ""}
                      disabled={addressValue === ""}
                    >
                      {t("FlashChat_Profile_Addfriend")}
                    </ConfirmButton>
                  )}
                </form>
              </Card>
            </ClickAwayListener>
          </ModalContainer>
        </BlackBackdrop>
      </Modal>
    </React.Fragment>
  );
};

export default AddFriendModal;
